import { Component, HostListener, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, Input } from '@angular/core';
import { CreateProjectService } from '../services/create-project.service';
import { Subject, Subscription } from 'rxjs';
import { finalize, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { RoleViewService } from '../services/role-view.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NotificationService } from '../services/notification.service';
import { SearchUserModalComponent } from './search-user-modal/search-user-modal.component';

type MemberVM = {
  // Stable identifier for selection tracking
  id: any;
  tm_dept_id: string;
  tm_workday_id: string;
  name: string;
  role: string;
  level: string;
  email?: string;
  isFavorite?: boolean;
  selected: boolean;
};

@Component({
  selector: 'app-select-department',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatDialogModule],
  templateUrl: './select-department.component.html',
  styleUrls: ['./select-department.component.css'],
 
})
export class SelectDepartmentComponent implements OnInit, OnDestroy {
  @Output() selectedDepartmentsChange = new EventEmitter<any[]>();
  @Output() departmentIdsChange = new EventEmitter<string[]>();
  @Output() selectedMembersChange = new EventEmitter<any[]>();
  @Input() preSelectedDepartmentIds: string[] = [];
  // Keyed by department id so a pre-selected member is only auto-checked for
  // the department it was actually selected in on the source project - not
  // for every department that happens to contain a member with the same id.
  @Input() preSelectedMemberIds: Record<string, string[]> = {};
  @Input() preSelectedDepartments: Array<{ id: string; name: string }> = [];

  departmentGroups: Array<{ letter: string; departments: any[] }> = [];
  selectedDepartments: any[] = [];
  selectedDepartmentIds: any[] = [];
  allMembers: Record<string, MemberVM[]> = {};
  fetchedDeptIds = new Set<string>();
  teamMembers: MemberVM[] = [];
  selectedMembers: MemberVM[] = [];
  memberCountMap = new Map<string, number>();
  searchText = '';
  memberSearchText = '';
  isAscending = true;
  isLoadingCounts = false;
  currentPage = 1;
  isLoadingMore = false;
  allDepartmentsLoaded = false;
  private readonly deptScroll$ = new Subject<Event>();
  private readonly departmentSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private departmentRequest?: Subscription;
  private hasInitializedDepartments = false;
  scrHeight: any;
  middleOpenState = new Map<string, boolean>();
  private readonly searchCollapsedDeptIds = new Set<string>();
  private searchCollapseQuery = '';
  userRole:any;
  workDayId: any;
  selectedDeptDetails:any
  private hasShownMissingCopiedDepartmentPopup = false;
  private readonly searchedMemberEmails = new Set<string>();
   
  constructor(
    private readonly createProjectService: CreateProjectService,
    private readonly cdr: ChangeDetectorRef,
    private readonly roleViewService: RoleViewService,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog
  ) {
    this.getScreenSize();
  }

  openSearchUserModal(): void {
    const dialogRef = this.dialog.open(SearchUserModalComponent, {
      width: '640px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe((result: { departments: Array<{ id: string; name: string; count?: number; isFavorite?: boolean }>; emails: string[] } | undefined) => {
      if (!result?.departments?.length) { return; }
      this.applySearchedDepartments(result.departments, result.emails);
    });
  }

  private applySearchedDepartments(depts: Array<{ id: string; name: string; count?: number; isFavorite?: boolean }>, emails: string[] = []): void {
    (emails || [])
      .map(e => e.trim().toLowerCase())
      .filter(Boolean)
      .forEach(e => this.searchedMemberEmails.add(e));

    let newlySelected = 0;
    depts.forEach(d => {
      const id = String(d.id);
      if (this.selectedDepartmentIds.includes(id)) {
        // Department already selected - its members may already be loaded, so
        // (re)apply the searched-email selection to them directly.
        this.applySearchedMemberSelectionForDept(id);
        return;
      }

      const dept = this.findDeptById(id) || {
        id,
        name: d.name,
        count: d.count ?? 0,
        isFavorite: d.isFavorite ?? false,
        _open: false
      };
      this.selectDepartment(dept);
      newlySelected++;
    });

    if (newlySelected > 0) {
      this.notificationService.showSuccess(`${newlySelected} department(s) selected.`);
    }
  }

  /**
   * Marks team members whose email matches a previously searched email as
   * selected, for a department whose members are already loaded in
   * `allMembers`. Used when the department was already selected before the
   * search (so `selectDepartment()`/member fetch won't run again).
   */
  private applySearchedMemberSelectionForDept(deptId: string): void {
    const members = this.allMembers[deptId];
    if (!members?.length || !this.searchedMemberEmails.size) { return; }
    let changed = false;
    members.forEach(m => {
      const email = (m.email || '').trim().toLowerCase();
      if (email && this.searchedMemberEmails.has(email) && !m.selected) {
        m.selected = true;
        changed = true;
      }
    });
    if (changed) { this.updateSelectedMembers(); }
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: any) {
    this.scrHeight = `${window.innerHeight - 250}px`;
  }
  ngOnInit() {
    this.workDayId = this.createProjectService.getWorkdayId();

    // Debounce scroll events so rapid mouse-wheel movement only triggers
    // one page-load check per 300 ms
    this.deptScroll$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(event => this.handleDeptScroll(event));
    this.departmentSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => this.reloadDepartments());
    this.userRole= this.roleViewService.getAdminAsRole();
    // Load any previously stored allMembers map early
    this.allMembers = this.createProjectService.getAllTeamMembers() || {};

    // Mark departments as fetched and clear any unsaved selection flags left from a prior visit
    Object.keys(this.allMembers).forEach(key => {
      this.fetchedDeptIds.add(key);
      (this.allMembers[key] || []).forEach(m => { m.selected = false; });
    });

    // Cache saved department/members metadata for restore
    const saved = this.createProjectService.getSelectedDepartment();
    if (saved?.selectedDepartments?.length) {
      this.selectedDeptDetails = saved;
      this.selectedDepartmentIds = saved.selectedDepartments.map((t: any) => String(t.id));
      this.selectedMembers = (saved.selectedMembers || []) as MemberVM[];
    } else {
      this.selectedDeptDetails = { selectedDepartments: [], selectedMembers: [] };
      this.selectedDepartmentIds = [];
      this.selectedMembers = [];
    }

    // Fetch departments list, then restore selection once groups are built
    this.loadDepartmentPage(1);
  }

  // Called from template — just pushes into the debounced stream
  onDeptListScroll(event: Event) {
    this.deptScroll$.next(event);
  }

  onDepartmentSearchChange(searchText: string) {
    this.departmentSearch$.next(searchText.trim());
  }

  // Actual logic runs after debounce settles
  private handleDeptScroll(event: Event) {
    const el = event.target as HTMLElement;
    const threshold = 100; // px from bottom to trigger next load
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    if (nearBottom && !this.isLoadingMore && !this.allDepartmentsLoaded) {
      this.currentPage++;
      this.loadDepartmentPage(this.currentPage);
    }
  }

  private reloadDepartments() {
    this.departmentRequest?.unsubscribe();
    this.isLoadingMore = false;
    this.currentPage = 1;
    this.allDepartmentsLoaded = false;
    this.loadDepartmentPage(1);
  }

  ngOnDestroy() {
    this.departmentRequest?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDepartmentPage(page: number) {
    if (this.isLoadingMore) { return; }
    this.isLoadingMore = true;
    const isFirstPage = page === 1;

    const sortOrder = this.isAscending ? 'asc' : 'desc';
    this.departmentRequest = this.createProjectService.fetchDepartments(this.workDayId, page, sortOrder, this.searchText)
      ?.pipe(finalize(() => { 
        this.isLoadingMore = false; 
        this.cdr.detectChanges();
       }))
      .subscribe({
        next: (resp: any) => {
          const departments = resp?.payload || [];

          // If fewer than 25 items came back, there are no more pages
          if (departments.length < 25) {
            this.allDepartmentsLoaded = true;
          }

          if (isFirstPage) {
            this.departmentGroups = [];
            this.memberCountMap.clear();
          }

          this.ingestDepartments(departments);

          if (isFirstPage && !this.hasInitializedDepartments) {
            this.hasInitializedDepartments = true;
            // Auto-select any previously selected/copied departments. Sort/pagination
            // is left untouched - if a selected department isn't on this page yet
            // (e.g. a non-favorite custom department further down the list), it will
            // still show correctly in the Team Members panel via a name-only
            // placeholder, and will show as checked once its real page is loaded.
            this.applyPreSelectedDepartments();
            this.notifyMissingCopiedDepartments();
            // After first page is loaded, restore any saved selection state
            this.restoreSelectedFromService();
          }

          console.info('Departments loaded:', 
            { page, count: departments.length, totalGroups: this.departmentGroups.length });
        },
        error: (error) => {
          console.trace('Error loading departments:', error);
        }
      });
  }

  private ingestDepartments(departments: any[]) {
    departments.forEach((d: any) => {
      const id = String(d.dept_id);
      if (this.findDeptById(id)) { return; } // already present, avoid duplicates

      const name = String(d.department_name);
      const isFav = this.toBooleanFlag(d.is_favourite ?? d.is_favorite);
      const afterDash = name.split('-')[1]?.trim();
      const letter = isFav ? '★' : (afterDash?.charAt(0) || name.charAt(0) || '•').toUpperCase();

      let group = this.departmentGroups.find(g => g.letter === letter);
      if (!group) {
        group = { letter, departments: [] };
        if (isFav) {
          this.departmentGroups.unshift(group);
        } else {
          this.departmentGroups.push(group);
        }
      }

      const count = Number(d.team_member_count || 0);

      group.departments.push({
        id,
        name,
        tm_name: d.tm_name,
        email_addresses: d.email_addresses,
        count,
        isFavorite: isFav,
        _open: false
      });

      this.memberCountMap.set(id, count);
    });
  }

  /**
   * Auto-select departments carried over from a copy/edit action. This never fetches
   * extra pages or reorders the list - sort/pagination stays exactly as the API
   * returns it. If a pre-selected department isn't loaded on the current page yet,
   * a lightweight name-only placeholder is selected instead so the Team Members
   * panel still shows it (and its members/favorites) correctly; once the real
   * department is loaded (e.g. by scrolling further), it will show as checked in
   * the list because selection is matched purely by id.
   */
  private applyPreSelectedDepartments() {
    if (!this.preSelectedDepartmentIds?.length) { return; }
    const nameById = new Map<string, string>(
      (this.preSelectedDepartments || []).map(d => [String(d.id), String(d.name || '')])
    );

    this.preSelectedDepartmentIds.forEach(rawId => {
      const id = String(rawId);
      if (this.selectedDepartmentIds.includes(id)) { return; }

      const dept = this.findDeptById(id) || {
        id,
        name: nameById.get(id) || id,
        count: 0,
        isFavorite: false,
        _open: false
      };
      this.selectDepartment(dept);
    });
  }

  selectDepartment(dept: any) {
    const idx = this.selectedDepartmentIds.indexOf(dept.id);
    if (idx === -1) {
      this.selectedDepartmentIds.push(dept.id);
      this.selectedDepartments = [dept, ...this.selectedDepartments.filter(d => d.id !== dept.id)];
      dept._open = false;
      dept._loadingMembers = true;

      // Set global loader while fetching team members
      this.isLoadingCounts = true;

      // Always fetch team members when department is selected (refresh state)
      this.getTeamMembersForSelected(dept.id);
    } else {
      this.selectedDepartmentIds.splice(idx, 1);
      this.selectedDepartments = this.selectedDepartments.filter(d => d.id !== dept.id);
      this.resetDeptState(dept.id);
    }
    this.createProjectService.setSelectedDepartmentId(this.selectedDepartmentIds);
  }

  private getTeamMembersForSelected(deptId: string, favoriteOverrides: Record<string, boolean> = {}) {
    // Only trust a 0 count when it's actually known (i.e. the department's page has
    // been loaded). A department not yet loaded on the current page (e.g. a
    // non-favorite custom department pre-selected via copy/edit) must still be
    // fetched so its real members/count show correctly in the Team Members panel.
    const hasKnownCount = this.memberCountMap.has(deptId);
    const deptCount = this.memberCountMap.get(deptId) || 0;
    const dept = this.findDeptById(deptId);
    if (dept) {dept.count = deptCount;}
    if (hasKnownCount && deptCount === 0) {
      this.allMembers[deptId] = [];
      this.fetchedDeptIds.add(deptId);
      this.updateTeamMembers();
      if (dept) {dept._loadingMembers = false;}
      this.isLoadingCounts = false;
      return;
    }

    if (dept) {dept._loadingMembers = true;}

    // Keep existing members visible until API returns so favorite toggles feel instant.
    this.updateTeamMembers();

    this.createProjectService.fetchTeamMembers(deptId, this.workDayId)
      .subscribe({
        next: (members) => {
          // new change Preserve current selection so marking favorites doesn't drop recent (non-preselected) selections
          const previouslySelectedIds = new Set(
            (this.allMembers[deptId] || []).filter(m => m.selected).map(m => String(m.id))
          );
          this.allMembers[deptId] = (members || []).map((m:any) => ({
            id: m.tm_id,
            // Associate with the selected department (incl. CUST-*) so validation matches
            tm_dept_id: String(deptId),
            tm_workday_id: m.tm_workday_id,
            name: String(m.tm_name || ''),
            role: String(m.tm_designation || ''),
            email: String(m.tm_email || m.email || m.tm_mail || ''),
            isFavorite: Object.prototype.hasOwnProperty.call(favoriteOverrides, String(m.tm_id))  //NOSONAR
              ? favoriteOverrides[String(m.tm_id)]
              : this.toBooleanFlag(m.isfavorite), 
            // new change level: String((m as any).tm_level || ''),
            selected: previouslySelectedIds.has(String(m.tm_id))
              || (this.preSelectedMemberIds[deptId] || []).includes(m.tm_workday_id?.toString()) // Keep current selection or pre-select
              || this.searchedMemberEmails.has(String(m.tm_email || m.email || m.tm_mail || '').trim().toLowerCase())
          }));
          this.sortMembersForDept(deptId);
          this.fetchedDeptIds.add(deptId);
          if (dept) { dept.count = this.allMembers[deptId].length; dept._loadingMembers = false; }
          this.updateTeamMembers();
          this.isLoadingCounts = false;
          this.createProjectService.setAllTeamMembers(this.allMembers);
        },
        error: (err) => { console.trace(err); if (dept){ dept._loadingMembers = false; this.isLoadingCounts = false;} }
      });
  }

  


  private findDeptById(deptId: string) {
    for (const group of this.departmentGroups) {
      const dept = group.departments.find(d => String(d.id) === deptId);
      if (dept) {return dept};
    }
    return null;
  }

  get filteredSelectedDepartmentsForTabs() {
    if (!this.memberSearchText.trim()) {return this.selectedDepartments};
    const q = this.memberSearchText.toLowerCase();
    return this.selectedDepartments.filter(d =>
      this.departmentMatchesMemberSearch(d, q) ||
      (this.allMembers[d.id] || []).some(m =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.role || '').toLowerCase().includes(q) ||
        (m.level || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
      )
    );
  }

  get selectedDepartmentsWithMembers() {
    return this.selectedDepartments.filter(d =>
      (this.allMembers[d.id] || []).some(m => m.selected)
    );
  }

  get filteredDepartmentsWithMembers() {
    const base = this.selectedDepartmentsWithMembers;
    const q = this.memberSearchText?.trim().toLowerCase();
    if (!q) {return base};

    return base.filter(d =>
      this.departmentMatchesMemberSearch(d, q) ||
      (this.allMembers[d.id] || []).some(m =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.role || '').toLowerCase().includes(q) ||
        (m.level || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
      )
    );
  }

  updateTeamMembers() {
    this.selectedDepartmentIds.forEach(deptId => this.sortMembersForDept(deptId));
    this.teamMembers = this.selectedDepartmentIds.reduce((acc: MemberVM[], id) =>
      acc.concat(this.allMembers[id] || []), []);
    this.updateSelectedMembers();
  }
updateSelectedMembers() {
  this.selectedMembers = this.selectedDepartmentIds.reduce((acc: MemberVM[], deptId) => {
    const members = (this.allMembers[deptId] || [])
      .filter(m => m.selected);
    return acc.concat(members);
  }, []);

  this.emitState();
}



  isDeptAllSelected(deptId: string): boolean {
    const members = this.allMembers[deptId] || [];
    if (members.length === 0){ return false};
    return members.every(m => m.selected);
  }

  getDeptSelectAllIcon(deptId: string): string {
    const members = this.allMembers[deptId] || [];
    if (members.length === 0) {return 'check_box_outline_blank'};
    return this.isDeptAllSelected(deptId) ? 'check_box' : 'check_box_outline_blank';
  }

  toggleDeptSelectAllButton(deptId: string) {
    const members = this.allMembers[deptId] || [];
    if (!members.length) {return};
    const shouldSelectAll = !members.every(m => m.selected);
    members.forEach(m => m.selected = shouldSelectAll);
    this.updateSelectedMembers();
    this.cdr.detectChanges();
  }

  getDeptSelectFavoriteIcon(deptId: string): string {
    const members = this.allMembers[deptId] || [];
    const favoriteMembers = members.filter(m => m.isFavorite);
    if (!favoriteMembers.length) {return 'check_box_outline_blank';}
    // Checked only when the selection is exactly the favorites (no non-favorite selected)
    const allFavoritesSelected = favoriteMembers.every(m => m.selected);
    const anyNonFavoriteSelected = members.some(m => !m.isFavorite && m.selected);
    return allFavoritesSelected && !anyNonFavoriteSelected ? 'check_box' : 'check_box_outline_blank';
  }

  hasFavoriteMembers(deptId: string): boolean {
    return (this.allMembers[deptId] || []).some(m => m.isFavorite);
  }

  toggleDeptSelectFavoriteButton(deptId: string) {
    const favoriteMembers = (this.allMembers[deptId] || []).filter(m => m.isFavorite);
    if (!favoriteMembers.length) {return;}
    const shouldSelectFavorite = !favoriteMembers.every(m => m.selected);
    favoriteMembers.forEach(m => m.selected = shouldSelectFavorite);
    this.updateSelectedMembers();
    this.cdr.detectChanges();
  }

  toggleTeamMemberSelection(member: MemberVM, event?: Event) {
    event?.stopPropagation();
    member.selected = !member.selected;
    this.updateSelectedMembers();
  }

  removeDepartment(deptOrId: any, event?: Event) {
    event?.stopPropagation();

    const dept = typeof deptOrId === 'string' || typeof deptOrId === 'number'
      ? this.findDeptById(String(deptOrId))
      : deptOrId;

    if (!dept || !this.selectedDepartmentIds.includes(dept.id)) {
      return;
    }

    this.selectDepartment(dept);
  }

  clearSelectedMembersOnly() {
    this.teamMembers.forEach(m => m.selected = false);
    this.selectedDepartments.forEach(d => d._open = false);
    this.middleOpenState.clear();
    this.updateSelectedMembers();
  }

  toggleFavoriteDept(dept: { id: string; isFavorite: boolean; _updating?: boolean }) {
    if (dept._updating) {return};
    this.isLoadingCounts = true;
    const body = { departmentId: dept.id, updatedBy: this.workDayId, isFavType: 'Department' as const };
    dept._updating = true;
    const prev = dept.isFavorite;
    dept.isFavorite = !dept.isFavorite;
    this.createProjectService.updateFavoriteDepartment(body).subscribe({
      next: () => { this.currentPage = 1; this.allDepartmentsLoaded = false; this.loadDepartmentPage(1); },
      error: (err) => { dept.isFavorite = prev; console.trace('Error:', err); this.isLoadingCounts = false; },
      complete: () => { dept._updating = false; this.isLoadingCounts = false; }
    });
  }

  teammemberFavorite(member: MemberVM, deptId: string, event?: Event){
    event?.stopPropagation();
    const prev = !!member.isFavorite;
    member.isFavorite = !member.isFavorite;
    this.sortMembersForDept(deptId);
    this.updateTeamMembers();
    this.cdr.detectChanges();
    this.createProjectService.setAllTeamMembers(this.allMembers);

    const body = {
      departmentId: deptId,
      updatedBy: this.workDayId,
      isFavType: 'TeamMember' as const,
      favUserId: member.id,
      isFavorite: member.isFavorite
    };

    this.createProjectService.updateFavoriteDepartment(body).subscribe({
      next: () => {
        this.getTeamMembersForSelected(deptId, { [String(member.id)]: !!member.isFavorite });
        this.createProjectService.setAllTeamMembers(this.allMembers);
      },
      error: (err) => {
        member.isFavorite = prev;
        this.sortMembersForDept(deptId);
        this.updateTeamMembers();
        this.createProjectService.setAllTeamMembers(this.allMembers);
        console.trace('Error:', err);
      }
    });
  }

  private toBooleanFlag(value: any): boolean {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
    }
    return value === true || value === 1;
  }

  private sortMembersForDept(deptId: string) {
    const members = this.allMembers[deptId] || [];
    members.sort((a, b) => {
      const favCompare = Number(!!b.isFavorite) - Number(!!a.isFavorite);
      if (favCompare !== 0) { return favCompare; }
      return (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  toggleSort() {
    this.isAscending = !this.isAscending;
    this.reloadDepartments();
  }

  toggleSelectedDept(deptOrId: any) {
    let dept = null;
    if (typeof deptOrId === 'string' || typeof deptOrId === 'number') { dept = this.findDeptById(String(deptOrId)); }
    else { dept = deptOrId; }
    if (dept) {dept._open = !dept._open};
  }

  isSelectedDeptOpen(deptOrId: any) {
    let dept = null;
    if (typeof deptOrId === 'string' || typeof deptOrId === 'number') { dept = this.findDeptById(String(deptOrId)); }
    else { dept = deptOrId; }
    return !!dept?._open;
  }

  toggleMiddleDept(deptId: string) {
    this.syncSearchCollapseState();

    const isOpening = !this.isMiddleDeptOpen(deptId);
    this.middleOpenState.set(deptId, isOpening);

    const query = this.memberSearchText?.trim().toLowerCase();
    if (query) {
      if (isOpening) {
        this.searchCollapsedDeptIds.delete(deptId);
      } else {
        this.searchCollapsedDeptIds.add(deptId);
      }
    }

    // Only fetch if we don't already have members for this department
    if (isOpening && !(this.allMembers[deptId] && this.allMembers[deptId].length > 0)) {
      this.getTeamMembersForSelected(deptId);
    }
  }



  isMiddleDeptOpen(deptId: string) {
    this.syncSearchCollapseState();

    if (this.middleOpenState.get(deptId)) {
      return true;
    }

    if (this.shouldAutoExpandDeptForSearch(deptId) && !this.searchCollapsedDeptIds.has(deptId)) {
      return true;
    }

    return false;
  }

  private syncSearchCollapseState() {
    const query = this.memberSearchText?.trim().toLowerCase() || '';
    if (query === this.searchCollapseQuery) {
      return;
    }

    this.searchCollapseQuery = query;
    this.searchCollapsedDeptIds.clear();
  }

  private shouldAutoExpandDeptForSearch(deptId: string): boolean {
    const query = this.memberSearchText?.trim().toLowerCase();
    if (!query) {
      return false;
    }

    const dept = this.findDeptById(deptId);
    if (dept && this.departmentMatchesMemberSearch(dept, query)) {
      return true;
    }

    return (this.allMembers[deptId] || []).some(m =>
      (m.name || '').toLowerCase().includes(query)
    );
  }

  private departmentMatchesMemberSearch(dept: any, query: string): boolean {
    return String(dept?.name || '').toLowerCase().includes(query);
  }

  isDeptLoadingMembers(deptId: string): boolean {
    const dept = this.findDeptById(deptId);
    return dept?._loadingMembers || false;
  }

  getMembersForDept(deptId: string) {
    const members = this.allMembers[deptId] || [];
    const query = this.memberSearchText?.trim().toLowerCase();

    if (!query) {
      return members;
    }

    // Keep all members visible but move matched names to the top while searching.
    return [...members].sort((a, b) => {
      const aMatch = (a.name || '').toLowerCase().includes(query);
      const bMatch = (b.name || '').toLowerCase().includes(query);

      if (aMatch !== bMatch) {
        return Number(bMatch) - Number(aMatch);
      }

      const favCompare = Number(!!b.isFavorite) - Number(!!a.isFavorite);
      if (favCompare !== 0) {
        return favCompare;
      }

      return (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  getSelectedMembersForDept(deptId: string) {
    return (this.allMembers[deptId] || []).filter(m => m.selected);
  }

  getSelectedCountForDept(deptId: string) {
    return this.getSelectedMembersForDept(deptId).length;
  }
getMemberCountForDept(deptId: string): number {
  // If members are already fetched, return actual length
  const members = this.allMembers[deptId];
  if (members && members.length > 0) {return members.length};

  // Fallback: return API-provided count
  return this.memberCountMap.get(deptId) || 0;
}
get totalTeamMemberCount(): number {
  return this.selectedDepartmentIds.reduce((total, deptId) => {
    const members = this.allMembers[deptId];
    if (members && members.length > 0) {
      return total + members.length; // already fetched
    }
    return total + (this.memberCountMap.get(deptId) || 0); // fallback
  }, 0);
}


  getInitials(name: string) {
    return (name || '')
      .split(' ')
      .filter(Boolean)
      .map(n => n[0])
      .join('')
      .toUpperCase();
  }

  private emitState() {
    this.departmentIdsChange.emit([...this.selectedDepartmentIds]);
    this.selectedMembersChange.emit([...this.selectedMembers]);
    this.selectedDepartmentsChange.emit([...this.selectedDepartments]);
  }

  private resetDeptState(deptId: string) {
    const cached = this.allMembers[deptId] || [];
    cached.forEach(m => (m.selected = false));
    const dept = this.findDeptById(deptId);
    if (dept) {dept._open = false;}
    this.middleOpenState.delete(deptId);
    this.updateTeamMembers();
    // Sync cleared selections back to service cache so remounts don't restore stale state
    this.createProjectService.setAllTeamMembers(this.allMembers);
  }

  private restoreSelectedFromService() {
    const saved = this.createProjectService.getSelectedDepartment();
    if (!saved) {
      return;
    }

    const savedDepts: any[] = saved.selectedDepartments || [];
    const savedMembers: any[] = saved.selectedMembers || [];

    if (!savedDepts.length) {
      return;
    }

    // Rebuild selectedDepartments from freshly loaded departmentGroups. Fall back to a
    // name-only placeholder (mirroring applyPreSelectedDepartments) when a saved
    // department isn't on the currently loaded page yet - e.g. a non-favorite custom
    // department sorted further down the list - so it isn't silently dropped here.
    this.selectedDepartments = savedDepts.map(sd => this.findDeptById(String(sd.id)) || {
      id: String(sd.id),
      name: String(sd.name || sd.id),
      count: 0,
      isFavorite: false,
      _open: false
    });

    this.selectedDepartmentIds = this.selectedDepartments.map(d => d.id);

    // Start from stored allMembers map (if any)
    this.allMembers = this.createProjectService.getAllTeamMembers() || {};

    // Apply saved member selections onto allMembers
    savedMembers.forEach((sm: any) => {
      const deptId = String(sm.tm_dept_id);
      const membersArr = this.allMembers[deptId] || [];
      const match = membersArr.find(m =>
        (sm.tm_workday_id && m.tm_workday_id === sm.tm_workday_id) ||
        (sm.id && m.id === sm.id)
      );
      if (match) {
        match.selected = true;
      }
    });

    this.updateTeamMembers();
    this.cdr.detectChanges();
  }

  private notifyMissingCopiedDepartments() {
    if (this.hasShownMissingCopiedDepartmentPopup) {
      return;
    }

    const sourceProjectId = this.createProjectService.getSourceProjectId();
    if (!sourceProjectId) {
      return;
    }

    this.hasShownMissingCopiedDepartmentPopup = true;
    this.createProjectService.fetchArchivedDepartmentsByProject(sourceProjectId).subscribe({
      next: (response: any) => {
        const payload = response?.payload?.response
          ?? response?.payload?.departments
          ?? response?.payload
          ?? response?.response
          ?? response;
        const archivedDepartments = Array.isArray(payload) ? payload : [];
        const fallbackById = new Map(
          (this.preSelectedDepartments || []).map(d => [String(d.id), String(d.name || '').trim()])
        );
        const archivedIds: string[] = [];
        const archivedNames = archivedDepartments
          .map((department: any) => {
            const departmentId = String(department?.dept_id || department?.department_id || '');
            if (departmentId) { archivedIds.push(departmentId); }
            return String(
              department?.department_name
              || department?.dept_name
              || department?.name
              || fallbackById.get(departmentId)
              || departmentId
            ).trim();
          })
          .filter((name: string, index: number, names: string[]) => !!name && names.indexOf(name) === index);

        if (!archivedNames.length) {
          return;
        }

        // These departments no longer exist in the API (deleted/archived) - they must
        // not remain checked/selected just because they were carried over from the
        // copied project's pre-selection.
        this.removeArchivedDepartmentsFromSelection(archivedIds);

        const formattedName = archivedNames.map(name => `'${name}'`).join(', ');
        const message = archivedNames.length === 1
          ? `The department ${formattedName} selected in the previous collection has been archived or is no longer available. Please select a new department to proceed.`
          : `The departments ${formattedName} selected in the previous collection have been archived or are no longer available. Please select new departments to proceed.`;
        this.notificationService.showPersistentToyotaInfo(message);
      },
      error: (error) => {
        console.trace('Error loading archived departments for copied project:', error);
      }
    });
  }

  /**
   * Drop departments that no longer exist in the API (archived/deleted) from the
   * current selection. Unlike removeDepartment(), this doesn't rely on findDeptById()
   * since an archived department was never loaded into departmentGroups - it only
   * ever existed as a name-only placeholder added by applyPreSelectedDepartments().
   */
  private removeArchivedDepartmentsFromSelection(archivedIds: string[]): void {
    if (!archivedIds.length) {
      return;
    }

    const idsToRemove = new Set(archivedIds.map(String));
    const hadSelection = this.selectedDepartmentIds.some(id => idsToRemove.has(String(id)));
    if (!hadSelection) {
      return;
    }

    this.selectedDepartmentIds = this.selectedDepartmentIds.filter(id => !idsToRemove.has(String(id)));
    this.selectedDepartments = this.selectedDepartments.filter(d => !idsToRemove.has(String(d.id)));

    idsToRemove.forEach(id => {
      delete this.allMembers[id];
      this.middleOpenState.delete(id);
    });

    this.updateTeamMembers();
    this.createProjectService.setSelectedDepartmentId(this.selectedDepartmentIds);
    this.createProjectService.setAllTeamMembers(this.allMembers);
    this.cdr.detectChanges();
  }

  trackByMemberId(_index: number, member: MemberVM): any { return member.id; }
  trackByDeptId(_index: number, dept: any): string { return String(dept.id); }
  trackByGroupLetter(_index: number, group: any): string { return group.letter; }
}
